import { factory } from '../../../../factory-api/factory'
import type { Coordinate, TypeAhead } from '@/generated-api'
import { defineStore, storeToRefs } from 'pinia'
import { getCountryMapping, sortArrayAlphabetically } from '../../../application/helpers'
import type { SelectItem } from '../../../interfaces/components/ISelectComponent'
import { useUserStore } from '../..'

export const useLocationStore = defineStore('locationStore', () => {
  const userStore = useUserStore()

  const locationTypeahead = ref<TypeAhead[]>([])
  const isLoadingTypeahead = ref<boolean>(false)
  const radiusValues = [
    { id: 10, name: '10 km' },
    { id: 25, name: '25 km' },
    { id: 50, name: '50 km' },
    { id: 75, name: '75 km' },
    { id: 100, name: '100 km' },
    { id: 150, name: '150 km' },
    { id: 200, name: '200 km' },
    { id: 300, name: '300 km' }
  ]
  const countries = [
    { id: 'DK', name: 'DK' },
    { id: 'NO', name: 'NO' },
    { id: 'SE', name: 'SE' },
    { id: 'FI', name: 'FI' }
  ]

  const selectedLocation = ref<TypeAhead>()
  const selectedRadius = ref<SelectItem>(radiusValues[2])
  const selectedCountry = ref<SelectItem>(countries[0])

  const getTypeaheadByCountry = async (country: string) => {
    isLoadingTypeahead.value = true
    const companySearchApi = await factory.getCompanySearchApi()
    const response = await companySearchApi.getFilters({
      country: country
    })

    if (response.typeahead) {
      const tempList = response.typeahead.filter((obj: TypeAhead) => obj.type === 'postal')
      tempList.forEach((test) => (test.name = test.postal + ' ' + test.city))
      locationTypeahead.value = sortArrayAlphabetically(tempList, 'city')
    }
    isLoadingTypeahead.value = false
  }

  const setSelectedLocation = (location: TypeAhead) => {
    selectedLocation.value = location
  }

  const setSelectedRadius = (radius: number) => {
    selectedRadius.value = { id: radius, name: radius + ' km' }
  }

  const setSelectedCountry = (country: SelectItem) => {
    selectedCountry.value = country
  }

  const emptySelectedLocation = () => {
    selectedLocation.value = undefined
  }

  const getAddressFromCoordinate = async (latitude: number, longitude: number) => {
    const tempSelectedLocation: TypeAhead = { lat: latitude, lon: longitude }
    const mapsApi = await factory.getMapsApi()
    const response = await mapsApi.addressFromCoordinate({
      latitude: latitude,
      longitude: longitude
    })

    if (response == null) {
      tempSelectedLocation.city = (latitude.toFixed(2) + ', ' + longitude.toFixed(2)).toString()
    } else if (
      response.city !== undefined &&
      response.city !== '' &&
      response.postalCode != undefined &&
      response.postalCode !== ''
    ) {
      tempSelectedLocation.city = response.city
      tempSelectedLocation.postal = response.postalCode
    } else if (
      //This is normally hit around coastlines
      response.city === undefined &&
      response.postalCode === undefined &&
      response.street === undefined
    ) {
      tempSelectedLocation.city = response.country
    } else if (
      (response.city === undefined && response.street === undefined) ||
      response.city === undefined
    ) {
      //This is normally hit when marker is in a forest, lake, bay etc., where we dont have street or a city
      //Sometimes we have a municipality, to use and sometimes we dont
      if (response.municipality !== undefined) {
        tempSelectedLocation.city = response.municipality
      } else {
        tempSelectedLocation.city = response.country
      }
    }

    tempSelectedLocation.city = tempSelectedLocation.city?.replace('Municipality', '')
    tempSelectedLocation.city = tempSelectedLocation.city?.replace('kommun', '')

    // when map is clicked or moved, we need to set country to the clicked location
    const countryMap = getCountryMapping(response.country)
    const countryToSelect = countries.find(
      (country) => country.id.toLocaleLowerCase() === countryMap.country.toLocaleLowerCase()
    )
    setSelectedCountry(countryToSelect ?? countries[0])

    setSelectedLocation(tempSelectedLocation)
  }

  const getCordinateFromAddress = async (
    country: string,
    postalCode: string,
    street: string
  ): Promise<Coordinate> => {
    const mapsApi = await factory.getMapsApi()
    return await mapsApi.coordinateFromAddress({
      country: country,
      postalcode: postalCode,
      street: street
    })
  }

  const getLocation = async () => {
    const { member } = storeToRefs(userStore)

    const memberLanguage = member.value.language ?? ''

    const defaultPosition = getCountryMapping(memberLanguage).mapInformation

    // If member address either is undefined or the address not a valid address, set default position
    const cordinates =
      !member.value.address && !member.value.zipCode
        ? null
        : await getCordinateFromAddress(
            memberLanguage,
            member.value.zipCode ?? '',
            member.value.address ?? ''
          )

    if (cordinates != null) {
      await getAddressFromCoordinate(cordinates.latitude ?? 0, cordinates.longitude ?? 0)
    } else {
      setSelectedLocation({
        city: defaultPosition.city,
        postal: defaultPosition.zipCode,
        lon: defaultPosition.longitude,
        lat: defaultPosition.latitude
      })
    }
  }

  return {
    locationTypeahead,
    selectedLocation,
    radiusValues,
    selectedRadius,
    countries,
    selectedCountry,
    isLoadingTypeahead,
    getTypeaheadByCountry,
    setSelectedLocation,
    setSelectedRadius,
    setSelectedCountry,
    emptySelectedLocation,
    getAddressFromCoordinate,
    getCordinateFromAddress,
    getLocation
  }
})
